/**
 * @file xeroLockMonth.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-11-02
 * @description Xero should be locked to the end of last month — one rule for
 *              the status bar, the dashboard notice and the save's question
 *              (utils/xeroLockMonth.js is the generated server copy;
 *              scripts/sync-shared-utils.js).
 *
 *   Cam, 15 Oct 2026: "The lock should be set for the month prior to the one
 *   we are currently in … if a user adds a bill or invoice or edits one where
 *   its date is in any month prior to the current month, can we always alert
 *   with a confirm dialogue regardless of the accounting lock status … Only
 *   apply this where actions should not be allowed by the lock."
 *
 *   - The expected lock is the last day of the month before the current
 *     London month (15/10/2026 → 30/09/2026; on 1 November it becomes
 *     31/10/2026). A lock later than that is fine too.
 *   - "The lock" is Xero's effective lock: the later of the period lock and
 *     the end-of-year lock (services/xeroLockService), the lock date itself
 *     locked.
 *   - A date needs the question when the expected lock would refuse it and
 *     the real one does not: on or before the expected lock, after the real
 *     one. When the real lock already refuses it, the save is refused as
 *     before (assertOpen) and nothing is asked.
 *
 *   Dates are London calendar days 'YYYY-MM-DD'. A plain day is taken as
 *   written; a timestamp or Date is read in London time.
 */

const LONDON = 'Europe/London';
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/** The code of the save's refusal until the question is answered. */
export const PRIOR_MONTH_CODE = 'PRIOR_MONTH_CONFIRM';

/** A value as its London calendar day 'YYYY-MM-DD', or null. */
export function lockMonthDay(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && ISO_DAY.test(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON, year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Today in London. `now` is injectable for tests. */
export function lockMonthToday(now = new Date()) {
  return lockMonthDay(now);
}

/** 'YYYY-MM-DD' as DD/MM/YYYY, the way every date in the system reads. */
export function lockMonthUkDate(day) {
  const m = ISO_DAY.exec(String(day || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** 'YYYY-MM-DD' as "September 2026". */
export function lockMonthName(day) {
  const m = ISO_DAY.exec(String(day || ''));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : '';
}

/**
 * The lock Xero should have: the last day of the month before today's
 * London month.
 */
export function expectedLockDate(today = lockMonthToday()) {
  const m = ISO_DAY.exec(String(today || ''));
  if (!m) return null;
  // Day 0 of this month is the last day of the one before.
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 0));
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * The effective lock date of a lock as the server holds it (getLock() /
 * useXeroLock): its effectiveLockDate, else the later of its period and
 * end-of-year locks. A plain 'YYYY-MM-DD' is taken as the lock itself.
 */
export function effectiveLockOf(lock) {
  if (!lock) return null;
  if (typeof lock === 'string') return lockMonthDay(lock);
  if (lock.effectiveLockDate) return lockMonthDay(lock.effectiveLockDate);
  const both = [lockMonthDay(lock.periodLockDate), lockMonthDay(lock.endOfYearLockDate)].filter(Boolean).sort();
  return both.length ? both[both.length - 1] : null;
}

/**
 * Is Xero locked to the end of last month?
 * @returns {{ expected: string, effective: string|null, ok: boolean, hasLock: boolean, text: string }}
 */
export function lockMonthState(lock, today = lockMonthToday()) {
  const expected = expectedLockDate(today);
  const effective = effectiveLockOf(lock);
  const ok = !!(effective && expected && effective >= expected);
  const exp = lockMonthUkDate(expected);
  let text;
  if (ok && effective === expected) text = `Xero is locked to ${exp}, the end of last month`;
  else if (ok) text = `Xero is locked to ${lockMonthUkDate(effective)}, after the end of last month (${exp})`;
  else if (effective) text = `Xero's lock is ${lockMonthUkDate(effective)} — it should be ${exp}, the end of last month`;
  else text = `No Xero lock date is set — it should be ${exp}, the end of last month`;
  return { expected, effective, ok, hasLock: !!effective, text };
}

/**
 * The dates the expected lock would refuse and the real one does not — on or
 * before the end of last month, after Xero's actual lock. Unique, sorted.
 * Empty when there is nothing to ask (no such date, or the real lock refuses
 * it already — the save is refused for that instead).
 */
export function priorMonthDates(dates, lock, today = lockMonthToday()) {
  const expected = expectedLockDate(today);
  if (!expected) return [];
  const effective = effectiveLockOf(lock);
  const days = (Array.isArray(dates) ? dates : [dates]).map(lockMonthDay).filter(Boolean);
  return [...new Set(days)]
    .filter((d) => d <= expected && (!effective || d > effective))
    .sort();
}

/** "a", "a and b", "a, b and c" */
function joinAnd(list) {
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * The confirm's words, or null when nothing needs asking.
 *
 *   "INV-D00123 is dated 25/09/2026, in September 2026. September 2026 should
 *    be locked in Xero by now (Xero's lock is 31/08/2026). Only go on if the
 *    lock has been taken off on purpose for this."
 *
 * @param {object} o
 * @param {string} o.what    the document ("INV-D00123", "This bill")
 * @param {Array}  o.dates   its dates, as the save checks them — on an edit
 *                           the date it has, then the date it is given
 * @param {object|string} o.lock  the lock (getLock() / useXeroLock) or its date
 * @returns {{ title, message, dates, months, expected, effective }|null}
 */
export function priorMonthQuestion({ what = 'This document', dates = [], lock = null, today = lockMonthToday() } = {}) {
  const hit = priorMonthDates(dates, lock, today);
  if (!hit.length) return null;
  const expected = expectedLockDate(today);
  const effective = effectiveLockOf(lock);
  const all = [];
  for (const d of (Array.isArray(dates) ? dates : [dates]).map(lockMonthDay)) {
    if (d && !all.includes(d)) all.push(d);
  }
  const inMonth = (d) => (hit.includes(d) ? `, in ${lockMonthName(d)}` : '');
  let lead;
  if (all.length >= 2) {
    lead = `${what} is dated ${lockMonthUkDate(all[0])}${inMonth(all[0])}; this changes it to ${lockMonthUkDate(all[all.length - 1])}${inMonth(all[all.length - 1])}.`;
  } else {
    lead = `${what} is dated ${lockMonthUkDate(all[0])}${inMonth(all[0])}.`;
  }
  const months = [...new Set(hit.map(lockMonthName))];
  const monthList = joinAnd(months);
  const lockWords = effective ? `Xero's lock is ${lockMonthUkDate(effective)}` : 'no Xero lock date is set';
  const message = `${lead} ${monthList} should be locked in Xero by now (${lockWords}). `
    + 'Only go on if the lock has been taken off on purpose for this.';
  return {
    title: `${monthList} should be locked in Xero`,
    message,
    dates: hit,
    months,
    expected,
    effective
  };
}

/**
 * One question for several documents at once (Bulk Add Bills' Accept): each
 * document's dates, one confirm naming them.
 * @param {Array<{ what: string, dates: Array }>} docs
 * @returns {{ title, message, dates, months, documents }|null}
 */
export function priorMonthQuestionForMany(docs, lock = null, today = lockMonthToday()) {
  const hits = (docs || [])
    .map((doc) => ({ what: doc.what, dates: priorMonthDates(doc.dates, lock, today) }))
    .filter((doc) => doc.dates.length);
  if (!hits.length) return null;
  if (hits.length === 1) {
    const one = (docs || []).find((doc) => doc.what === hits[0].what);
    const q = priorMonthQuestion({ what: one.what, dates: one.dates, lock, today });
    return q && { ...q, documents: [hits[0].what] };
  }
  const dates = [...new Set(hits.flatMap((doc) => doc.dates))].sort();
  const months = [...new Set(dates.map(lockMonthName))];
  const monthList = joinAnd(months);
  const effective = effectiveLockOf(lock);
  const lockWords = effective ? `Xero's lock is ${lockMonthUkDate(effective)}` : 'no Xero lock date is set';
  // A count, not every document named (Cam, 2 Nov 2026 — Till Sales listed
  // dozens of sales in the question): the list goes in `items`, which the
  // question's window opens in a scrollable list of its own.
  return {
    title: `${monthList} should be locked in Xero`,
    message: `${hits.length} of these are dated in ${monthList}. `
      + `${monthList} should be locked in Xero by now (${lockWords}). `
      + 'Only go on if the lock has been taken off on purpose for this.',
    dates,
    months,
    expected: expectedLockDate(today),
    effective,
    documents: hits.map((doc) => doc.what),
    items: hits.map((doc) => ({ what: doc.what, dates: doc.dates }))
  };
}
